import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogOut,
  Search,
  ArrowDownUp,
  Download,
  Users,
  MessageSquare,
  ChevronDown,
  Loader2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  ExternalLink,
  RefreshCw,
  FileText,
  Eye,
  Pencil,
  Save,
  ShieldCheck,
  History,
  RotateCcw,
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Link2,
  Code,
  Quote,
  Table as TableIcon,
  Minus,
  CheckCircle2,
  Languages,
  Copy,
  Trash2,
  Star,
  ThumbsUp,
  ThumbsDown,
  Clock,
  Newspaper,
  Plus,
  HelpCircle,
  Package,
  ArrowLeft,
  Lock,
  X,
  AlertCircle,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import { useAuth, authHeaders, formatApiErrorDetail } from "@/lib/auth";
import FAQPanel from "@/components/admin/FAQPanel";
import BetaSignupsPanel from "@/components/admin/BetaSignupsPanel";
import BetaAPKPanel from "@/components/admin/BetaAPKPanel";
import ConsentLogPanel from "@/components/admin/ConsentLogPanel";

const TABS = [
  { id: "beta", label: "Beta Signups", icon: Users },
  { id: "apk", label: "Beta APK", icon: Package },
  { id: "contact", label: "Contact Messages", icon: MessageSquare },
  { id: "testimonials", label: "Testimonials", icon: Star },
  { id: "legal", label: "Terms & Policies", icon: FileText },
  { id: "consent", label: "Consent Log", icon: ShieldCheck },
  { id: "blog", label: "Blog", icon: Newspaper },
  { id: "faq", label: "FAQ", icon: HelpCircle },
];

export default function AdminDashboard() {
  const { token, email, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("beta");
  const [stats, setStats] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);

  useEffect(() => {
    document.title = "Careable 360+ Admin";
  }, []);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/stats", { headers: authHeaders() });
        setStats(data);
      } catch {
        /* silent */
      }
    })();
  }, [activeTab, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4FAF8]">
        <Loader2 className="w-6 h-6 animate-spin text-[#1F4E5F]" />
      </div>
    );
  }
  if (!token) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#F4FAF8]">
      <Topbar
        email={email}
        onLogout={() => {
          logout();
          navigate("/admin/login", { replace: true });
        }}
        onChangePassword={() => setShowChangePassword(true)}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        <header className="mb-8">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0E2A36]">
            Welcome back 👋
          </h1>
          <p className="text-[#3D5560] text-sm mt-1">
            Browse incoming beta signups and contact messages.
          </p>
        </header>

        <section
          data-testid="admin-stats"
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          <StatCard
            icon={Users}
            label="Total Beta Signups"
            value={stats?.total_beta_signups ?? "—"}
            tone="brand"
          />
          <StatCard
            icon={Users}
            label="Signups · last 7 days"
            value={stats?.signups_last_7d ?? "—"}
            tone="green"
          />
          <StatCard
            icon={MessageSquare}
            label="Total Contact Messages"
            value={stats?.total_contact_messages ?? "—"}
            tone="amber"
          />
          <StatCard
            icon={MessageSquare}
            label="Messages · last 7 days"
            value={stats?.messages_last_7d ?? "—"}
            tone="brand-soft"
          />
        </section>

        <div className="inline-flex items-center gap-1 bg-white border border-[#C9DDD6] rounded-full p-1 mb-6">
          {TABS.map((t) => {
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  active
                    ? "bg-[#1F4E5F] text-[#F4FAF8]"
                    : "text-[#3D5560] hover:bg-[#E1EEEA]"
                }`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {activeTab === "beta" && <BetaSignupsPanel />}
        {activeTab === "apk" && <BetaAPKPanel />}
        {activeTab === "contact" && <ContactTable />}
        {activeTab === "testimonials" && <TestimonialsPanel />}
        {activeTab === "legal" && <LegalEditor />}
        {activeTab === "consent" && <ConsentLogPanel />}
        {activeTab === "blog" && <BlogPanel />}
        {activeTab === "faq" && <FAQPanel />}
      </main>

      <AnimatePresence>
        {showChangePassword && (
          <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}

function Topbar({ email, onLogout, onChangePassword }) {
  return (
    <div className="bg-white border-b border-[#C9DDD6] sticky top-0 z-30 backdrop-blur-xl bg-white/85">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img
            src="https://customer-assets.emergentagent.com/job_design-upgrade-217/artifacts/fzqlgxrv_App%20icon.png"
            alt="Careable"
            className="w-9 h-9 rounded-xl object-cover"
          />
          <div className="leading-tight">
            <p className="font-display font-extrabold text-[#1F4E5F] text-base">
              Careable
            </p>
            <p className="text-[9px] font-bold tracking-[0.18em] text-[#E8A33D] uppercase">
              Admin Console
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline text-xs text-[#3D5560]">{email}</span>
          <button
            data-testid="admin-change-password"
            onClick={onChangePassword}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold text-[#1F4E5F] hover:bg-[#E1EEEA] transition-colors"
          >
            <Lock className="w-4 h-4" /> Change password
          </button>
          <button
            data-testid="admin-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold text-[#1F4E5F] hover:bg-[#E1EEEA] transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState({ type: "idle", message: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      setStatus({ type: "error", message: "New password must be at least 8 characters." });
      return;
    }
    if (next !== confirm) {
      setStatus({ type: "error", message: "New passwords do not match." });
      return;
    }
    setStatus({ type: "loading", message: "" });
    try {
      await api.post(
        "/admin/change-password",
        { current_password: current, new_password: next },
        { headers: authHeaders() }
      );
      setStatus({ type: "done", message: "Password changed successfully." });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setStatus({ type: "error", message: msg });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="change-password-modal"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.98 }}
        transition={{ duration: 0.18 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-7 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="change-password-close"
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-[#6A8290] hover:bg-[#E1EEEA]"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {status.type === "done" ? (
          <div className="text-center space-y-3 py-2">
            <div className="w-14 h-14 rounded-2xl bg-[#E1F0E7] flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-7 h-7 text-[#2E7D52]" />
            </div>
            <h2 className="font-display text-xl font-bold text-[#0E2A36]">Password updated</h2>
            <p className="text-sm text-[#48606B]">{status.message}</p>
            <button
              data-testid="change-password-done"
              onClick={onClose}
              className="mt-2 inline-flex items-center gap-2 bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="change-password-form" className="space-y-4">
            <div>
              <h2 className="font-display text-xl font-bold text-[#0E2A36]">Change password</h2>
              <p className="text-sm text-[#6A8290] mt-1">
                Enter your current password and choose a new one.
              </p>
            </div>

            <CPField
              id="cp-current"
              label="Current password"
              testid="change-password-current"
              value={current}
              onChange={setCurrent}
              autoComplete="current-password"
            />
            <CPField
              id="cp-new"
              label="New password"
              testid="change-password-new"
              value={next}
              onChange={setNext}
              autoComplete="new-password"
            />
            <CPField
              id="cp-confirm"
              label="Confirm new password"
              testid="change-password-confirm"
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
            />

            {status.type === "error" && (
              <div
                data-testid="change-password-error"
                className="flex items-center gap-2 text-[#C84444] bg-[#E55B5B]/10 border border-[#E55B5B]/25 rounded-xl px-3 py-2.5 text-sm"
              >
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{status.message}</span>
              </div>
            )}

            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                data-testid="change-password-cancel"
                onClick={onClose}
                className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-full text-sm font-semibold text-[#1F4E5F] bg-[#E1EEEA] hover:bg-[#D0E2DC] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="change-password-submit"
                disabled={status.type === "loading"}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] px-4 py-2.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-70"
              >
                {status.type === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                  </>
                ) : (
                  "Update password"
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
}

function CPField({ id, label, testid, value, onChange, autoComplete }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-bold uppercase tracking-[0.14em] text-[#3D5560] mb-2"
      >
        {label}
      </label>
      <div className="relative">
        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A8290]" />
        <input
          id={id}
          type="password"
          data-testid={testid}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••"
          className="w-full pl-10 pr-4 py-3 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl text-[15px] focus:outline-none focus:border-[#1F4E5F] focus:ring-2 focus:ring-[#5BAE71]/30 transition"
        />
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  const tones = {
    brand: "bg-[#1F4E5F] text-[#F4FAF8] border-[#1F4E5F]",
    green: "bg-[#5BAE71] text-white border-[#5BAE71]",
    amber: "bg-[#E8A33D] text-white border-[#E8A33D]",
    "brand-soft": "bg-white text-[#0E2A36] border-[#C9DDD6]",
  };
  const isDark = tone !== "brand-soft";
  return (
    <div className={`rounded-2xl border p-5 ${tones[tone]}`}>
      <div className="flex items-center justify-between mb-3">
        <div
          className={`w-9 h-9 rounded-xl flex items-center justify-center ${
            isDark ? "bg-white/15" : "bg-[#E1EEEA]"
          }`}
        >
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="font-display text-3xl font-bold leading-none">{value}</p>
      <p
        className={`mt-2 text-xs font-medium ${
          isDark ? "text-white/80" : "text-[#6A8290]"
        }`}
      >
        {label}
      </p>
    </div>
  );
}

/* ============ BETA SIGNUPS TABLE ============ */
function BetaTable() {
  return (
    <DataPanel
      title="Beta Signups"
      endpoint="/admin/beta-signups"
      csvName="careable-beta-signups"
      testIdPrefix="beta"
      columns={[
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "role", label: "Role" },
        { key: "city", label: "City" },
        { key: "senior_location", label: "Parent's Location" },
        { key: "consent_privacy_notice", label: "Consent", type: "consent" },
        { key: "created_at", label: "Joined", type: "date" },
      ]}
      renderExpanded={(row) => (
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Detail icon={Mail} label="Email" value={row.email} link={`mailto:${row.email}`} />
          <Detail icon={Phone} label="Phone" value={row.phone || "—"} link={row.phone ? `tel:${row.phone}` : null} />
          <Detail icon={MapPin} label="City" value={row.city || "—"} />
          <Detail icon={MapPin} label="Parent's Location" value={row.senior_location || "—"} />
          <Detail icon={Users} label="Role" value={roleLabel(row.role)} />
          <Detail icon={Calendar} label="Joined" value={fmtDate(row.created_at)} />
          <Detail
            icon={ShieldCheck}
            label="Privacy Consent"
            value={
              row.consent_privacy_notice
                ? `Accepted${row.consent_recorded_at ? ` · ${fmtDate(row.consent_recorded_at)}` : ""}`
                : "Not recorded"
            }
          />
        </div>
      )}
    />
  );
}

function ContactTable() {
  return (
    <DataPanel
      title="Contact Messages"
      endpoint="/admin/contact-messages"
      csvName="careable-contact-messages"
      testIdPrefix="contact"
      columns={[
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "subject", label: "Subject" },
        { key: "created_at", label: "Received", type: "date" },
      ]}
      renderExpanded={(row) => (
        <div className="space-y-4 text-sm">
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
            <Detail icon={Mail} label="Email" value={row.email} link={`mailto:${row.email}`} />
            <Detail icon={Calendar} label="Received" value={fmtDate(row.created_at)} />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#6A8290] mb-1.5">
              Message
            </p>
            <div className="rounded-2xl bg-[#F4FAF8] border border-[#C9DDD6] p-4 whitespace-pre-wrap text-[#0E2A36] leading-relaxed">
              {row.message}
            </div>
          </div>
        </div>
      )}
    />
  );
}

/* ============ Reusable Data Panel ============ */
function DataPanel({ title, endpoint, csvName, columns, renderExpanded, testIdPrefix }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [sortDesc, setSortDesc] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get(endpoint, { headers: authHeaders() });
        if (!cancelled) setRows(data || []);
      } catch (err) {
        if (!cancelled)
          setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [endpoint]);

  const fetchRows = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(endpoint, { headers: authHeaders() });
      setRows(data || []);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = q
      ? rows.filter((r) =>
          [r.name, r.email, r.subject, r.city, r.senior_location, r.message]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q))
        )
      : rows;
    list = [...list].sort((a, b) => {
      const at = new Date(a.created_at).getTime();
      const bt = new Date(b.created_at).getTime();
      return sortDesc ? bt - at : at - bt;
    });
    return list;
  }, [rows, query, sortDesc]);

  const onExportCsv = () => {
    const header = columns.map((c) => c.label);
    const lines = [header.join(",")];
    filtered.forEach((r) => {
      const row = columns.map((c) => {
        const v = c.type === "date" ? fmtDate(r[c.key]) : (r[c.key] ?? "");
        return `"${String(v).replace(/"/g, '""')}"`;
      });
      lines.push(row.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${csvName}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      data-testid={`${testIdPrefix}-panel`}
      className="bg-white border border-[#C9DDD6] rounded-3xl overflow-hidden"
    >
      <div className="p-5 border-b border-[#C9DDD6] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-[#0E2A36]">{title}</h2>
          <p className="text-xs text-[#6A8290]">
            {filtered.length} of {rows.length} record{rows.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A8290]" />
            <input
              data-testid={`${testIdPrefix}-search`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email…"
              className="pl-9 pr-3 py-2 bg-[#F4FAF8] border border-[#C9DDD6] rounded-full text-sm w-56 focus:outline-none focus:border-[#1F4E5F]"
            />
          </div>
          <button
            data-testid={`${testIdPrefix}-sort`}
            onClick={() => setSortDesc((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold bg-[#F4FAF8] border border-[#C9DDD6] text-[#3D5560] hover:border-[#1F4E5F]"
            title="Toggle date sort"
          >
            <ArrowDownUp className="w-3.5 h-3.5" />
            {sortDesc ? "Newest" : "Oldest"}
          </button>
          <button
            data-testid={`${testIdPrefix}-refresh`}
            onClick={fetchRows}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold bg-[#F4FAF8] border border-[#C9DDD6] text-[#3D5560] hover:border-[#1F4E5F]"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            data-testid={`${testIdPrefix}-export`}
            onClick={onExportCsv}
            disabled={!filtered.length}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="py-16 flex items-center justify-center text-[#6A8290] text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="py-16 text-center text-[#C84444] text-sm">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-[#0E2A36] font-semibold">No records yet</p>
            <p className="text-sm text-[#6A8290] mt-1">
              {query ? "Try a different search." : "New entries will appear here."}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F4FAF8]">
              <tr>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className="text-left px-5 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A8290]"
                  >
                    {c.label}
                  </th>
                ))}
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E1EEEA]">
              {filtered.map((r) => {
                const isOpen = expandedId === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr
                      data-testid={`${testIdPrefix}-row-${r.id}`}
                      onClick={() => setExpandedId(isOpen ? null : r.id)}
                      className="cursor-pointer hover:bg-[#F4FAF8] transition-colors"
                    >
                      {columns.map((c) => {
                        let v;
                        if (c.type === "date") v = fmtDate(r[c.key]);
                        else if (c.type === "consent") v = r[c.key];
                        else v = r[c.key] ?? "—";
                        return (
                          <td
                            key={c.key}
                            className="px-5 py-3.5 align-top text-[#0E2A36]"
                          >
                            {c.key === "role" ? (
                              roleLabel(v)
                            ) : c.type === "consent" ? (
                              v ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#5BAE71]/15 text-[#2D7A4A] text-xs font-semibold">
                                  <CheckCircle2 className="w-3 h-3" /> Accepted
                                </span>
                              ) : (
                                <span className="text-[#6A8290] text-xs">—</span>
                              )
                            ) : (
                              v || "—"
                            )}
                          </td>
                        );
                      })}
                      <td className="px-3">
                        <ChevronDown
                          className={`w-4 h-4 text-[#6A8290] transition-transform ${
                            isOpen ? "rotate-180" : ""
                          }`}
                        />
                      </td>
                    </tr>
                    <AnimatePresence>
                      {isOpen && (
                        <tr
                          key={`${r.id}-detail`}
                          data-testid={`${testIdPrefix}-row-detail-${r.id}`}
                        >
                          <td colSpan={columns.length + 1} className="bg-[#F4FAF8] px-5 py-5">
                            <motion.div
                              initial={{ opacity: 0, y: -6 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -6 }}
                              transition={{ duration: 0.2 }}
                            >
                              {renderExpanded(r)}
                            </motion.div>
                          </td>
                        </tr>
                      )}
                    </AnimatePresence>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Detail({ icon: Icon, label, value, link }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-8 h-8 rounded-lg bg-white border border-[#C9DDD6] flex items-center justify-center shrink-0">
        <Icon className="w-3.5 h-3.5 text-[#1F4E5F]" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#6A8290]">
          {label}
        </p>
        {link ? (
          <a
            href={link}
            className="text-[#1F4E5F] hover:underline font-medium text-sm break-words inline-flex items-center gap-1"
          >
            {value}
            <ExternalLink className="w-3 h-3" />
          </a>
        ) : (
          <p className="text-[#0E2A36] font-medium text-sm break-words">{value}</p>
        )}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function roleLabel(r) {
  const map = {
    adult_child: "Adult Child",
    senior: "Senior / Parent",
    caregiver: "Caregiver / Family",
  };
  return map[r] || r;
}

/* ============ LEGAL DOCUMENTS EDITOR ============ */
const LEGAL_SECTIONS = [
  { slug: "privacy-notice", label: "Privacy Notice", sub: "DPDP Act, 2023" },
  { slug: "privacy-policy", label: "Privacy Policy", sub: "How we use your data" },
  { slug: "terms-of-service", label: "Terms of Service", sub: "Rules of using Careable" },
];

const TOOLBAR_BUTTONS = [
  { key: "h1", icon: Heading1, label: "Heading 1", action: { type: "linePrefix", prefix: "# " } },
  { key: "h2", icon: Heading2, label: "Heading 2", action: { type: "linePrefix", prefix: "## " } },
  { key: "h3", icon: Heading3, label: "Heading 3", action: { type: "linePrefix", prefix: "### " } },
  { divider: true },
  { key: "bold", icon: Bold, label: "Bold", action: { type: "wrap", prefix: "**", suffix: "**", placeholder: "bold text" } },
  { key: "italic", icon: Italic, label: "Italic", action: { type: "wrap", prefix: "_", suffix: "_", placeholder: "italic text" } },
  { key: "code", icon: Code, label: "Inline code", action: { type: "wrap", prefix: "`", suffix: "`", placeholder: "code" } },
  { divider: true },
  { key: "ul", icon: List, label: "Bulleted list", action: { type: "linePrefix", prefix: "- " } },
  { key: "ol", icon: ListOrdered, label: "Numbered list", action: { type: "orderedList" } },
  { key: "quote", icon: Quote, label: "Quote", action: { type: "linePrefix", prefix: "> " } },
  { divider: true },
  { key: "link", icon: Link2, label: "Insert link", action: { type: "link" } },
  { key: "table", icon: TableIcon, label: "Insert table", action: { type: "block", block: "\n| Column A | Column B | Column C |\n| --- | --- | --- |\n| Row 1A | Row 1B | Row 1C |\n| Row 2A | Row 2B | Row 2C |\n" } },
  { key: "hr", icon: Minus, label: "Horizontal rule", action: { type: "block", block: "\n\n---\n\n" } },
];

function runMarkdownAction(action, selected) {
  if (action.type === "wrap") {
    const txt = selected || action.placeholder || "";
    const text = `${action.prefix}${txt}${action.suffix}`;
    return { text, selStart: action.prefix.length, selEnd: action.prefix.length + txt.length };
  }
  if (action.type === "linePrefix") {
    const lines = (selected || "list item").split("\n");
    const text = lines.map((l) => `${action.prefix}${l}`).join("\n");
    return { text, selStart: action.prefix.length, selEnd: text.length };
  }
  if (action.type === "orderedList") {
    const lines = (selected || "first item\nsecond item").split("\n");
    const text = lines.map((l, i) => `${i + 1}. ${l}`).join("\n");
    return { text, selStart: 3, selEnd: text.length };
  }
  if (action.type === "block") {
    return { text: action.block, selStart: 0, selEnd: action.block.length };
  }
  if (action.type === "link") {
    const url = window.prompt("Enter URL", "https://");
    if (!url) return { text: selected, selStart: 0, selEnd: selected.length };
    const label = selected || "link text";
    const text = `[${label}](${url})`;
    return { text, selStart: 1, selEnd: 1 + label.length };
  }
  return { text: selected, selStart: 0, selEnd: selected.length };
}

function MarkdownToolbar({ onAction }) {
  return (
    <div
      data-testid="legal-toolbar"
      className="flex flex-wrap items-center gap-0.5 p-1.5 rounded-xl bg-[#F4FAF8] border border-[#C9DDD6]"
    >
      {TOOLBAR_BUTTONS.map((b, i) =>
        b.divider ? (
          <span key={`d${i}`} className="w-px h-5 bg-[#C9DDD6] mx-1" />
        ) : (
          <button
            key={b.key}
            type="button"
            data-testid={`legal-toolbar-${b.key}`}
            onClick={() => onAction(b.action)}
            title={b.label}
            aria-label={b.label}
            className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-[#3D5560] hover:bg-[#E1EEEA] hover:text-[#1F4E5F] transition-colors"
          >
            <b.icon className="w-4 h-4" />
          </button>
        )
      )}
    </div>
  );
}

function LegalEditor() {
  const [docs, setDocs] = useState({});
  const [languages, setLanguages] = useState([
    { code: "en", label: "English", native: "English", available: {} },
  ]);
  const [activeSlug, setActiveSlug] = useState("privacy-notice");
  const [activeLang, setActiveLang] = useState("en");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [draftTitle, setDraftTitle] = useState("");
  const [draftContent, setDraftContent] = useState("");
  const [mode, setMode] = useState("split"); // edit | split | preview
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [viewingVersion, setViewingVersion] = useState(null);
  const textareaRef = useRef(null);

  const key = (slug, lang) => `${slug}:${lang}`;

  const refreshLanguages = async () => {
    try {
      const { data } = await api.get("/legal/languages");
      setLanguages(data || []);
    } catch {
      // ignore — keep current list
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: enDocs }, { data: langs }] = await Promise.all([
          api.get("/legal", { params: { lang: "en" } }),
          api.get("/legal/languages"),
        ]);
        if (cancelled) return;
        const map = {};
        (enDocs || []).forEach((d) => (map[key(d.slug, "en")] = d));
        setDocs(map);
        setLanguages(langs || []);
        const initial = map[key("privacy-notice", "en")];
        setDraftTitle(initial?.title || "");
        setDraftContent(initial?.content || "");
      } catch (err) {
        if (!cancelled)
          setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadDoc = async (slug, lang) => {
    try {
      const { data } = await api.get(`/legal/${slug}`, { params: { lang } });
      if (data.is_fallback) {
        // No real translation exists yet — leave the (slug,lang) entry empty so the UI shows empty state
        setDocs((prev) => {
          const next = { ...prev };
          // ensure English copy is cached
          next[key(slug, "en")] = { ...data, is_fallback: false };
          // mark non-en as empty (null sentinel)
          if (lang !== "en") next[key(slug, lang)] = null;
          return next;
        });
        return null;
      }
      setDocs((prev) => ({ ...prev, [key(slug, lang)]: data }));
      return data;
    } catch (err) {
      // 404 means no translation at all (e.g. en doc missing)
      if (err?.response?.status === 404 && lang !== "en") {
        setDocs((prev) => ({ ...prev, [key(slug, lang)]: null }));
        return null;
      }
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
      return null;
    }
  };

  const loadHistory = async (slug, lang) => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/admin/legal/${slug}/history`, {
        params: { lang },
        headers: authHeaders(),
      });
      setHistory(data || []);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const applyDraft = (doc) => {
    setDraftTitle(doc?.title || "");
    setDraftContent(doc?.content || "");
  };

  const switchSection = async (slug) => {
    setActiveSlug(slug);
    setError("");
    setSuccess("");
    setViewingVersion(null);
    const cached = docs[key(slug, activeLang)];
    if (cached !== undefined) {
      applyDraft(cached);
    } else {
      const doc = await loadDoc(slug, activeLang);
      applyDraft(doc);
    }
    if (showHistory) loadHistory(slug, activeLang);
  };

  const switchLang = async (lang) => {
    setActiveLang(lang);
    setError("");
    setSuccess("");
    setViewingVersion(null);
    const cached = docs[key(activeSlug, lang)];
    if (cached !== undefined) {
      applyDraft(cached);
    } else {
      const doc = await loadDoc(activeSlug, lang);
      applyDraft(doc);
    }
    if (showHistory) loadHistory(activeSlug, lang);
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    setViewingVersion(null);
    if (next) loadHistory(activeSlug, activeLang);
  };

  const dirty = useMemo(() => {
    const current = docs[key(activeSlug, activeLang)];
    const baselineTitle = current?.title || "";
    const baselineContent = current?.content || "";
    return draftTitle !== baselineTitle || draftContent !== baselineContent;
  }, [draftTitle, draftContent, docs, activeSlug, activeLang]);

  const copyFromEnglish = () => {
    const en = docs[key(activeSlug, "en")];
    if (!en) return;
    setDraftTitle(en.title || "");
    setDraftContent(en.content || "");
    setSuccess("Copied English as a starting point — translate and click Save.");
  };

  const onSave = async () => {
    if (!draftTitle.trim() || !draftContent.trim()) {
      setError("Title and content are both required.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await api.put(
        `/admin/legal/${activeSlug}`,
        { title: draftTitle.trim(), content: draftContent },
        { params: { lang: activeLang }, headers: authHeaders() }
      );
      setDocs((prev) => ({ ...prev, [key(activeSlug, activeLang)]: data }));
      setSuccess(
        activeLang === "en"
          ? "Saved. Previous version archived in history."
          : `Saved in ${languages.find((l) => l.code === activeLang)?.native || activeLang}. Previous version archived in history.`
      );
      if (showHistory) loadHistory(activeSlug, activeLang);
      refreshLanguages();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const onRestore = async (versionId) => {
    if (!window.confirm("Restore this version? Your current draft will be replaced and the existing version will be archived.")) {
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await api.post(
        `/admin/legal/${activeSlug}/restore/${versionId}`,
        {},
        { headers: authHeaders() }
      );
      setDocs((prev) => ({ ...prev, [key(activeSlug, activeLang)]: data }));
      setDraftTitle(data.title);
      setDraftContent(data.content);
      setSuccess("Version restored. Previous live document is now archived.");
      setViewingVersion(null);
      loadHistory(activeSlug, activeLang);
      refreshLanguages();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDeleteTranslation = async () => {
    if (activeLang === "en") return;
    const langInfo = languages.find((l) => l.code === activeLang);
    if (!window.confirm(`Delete the ${langInfo?.native || activeLang} translation of this document? The English version stays untouched and the translation is archived in history.`)) {
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await api.delete(`/admin/legal/${activeSlug}`, {
        params: { lang: activeLang },
        headers: authHeaders(),
      });
      setDocs((prev) => ({ ...prev, [key(activeSlug, activeLang)]: null }));
      setDraftTitle("");
      setDraftContent("");
      setSuccess("Translation deleted. Public pages will now fall back to English.");
      if (showHistory) loadHistory(activeSlug, activeLang);
      refreshLanguages();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  // === Markdown toolbar action handler ===
  const onToolbarAction = (action) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const value = ta.value;
    const before = value.slice(0, start);
    const selected = value.slice(start, end);
    const after = value.slice(end);
    const result = runMarkdownAction(action, selected);
    const next = before + result.text + after;
    setDraftContent(next);
    requestAnimationFrame(() => {
      const node = textareaRef.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(before.length + result.selStart, before.length + result.selEnd);
    });
  };

  if (loading) {
    return (
      <div
        data-testid="legal-panel-loading"
        className="bg-white border border-[#C9DDD6] rounded-3xl py-20 flex items-center justify-center text-[#6A8290] text-sm"
      >
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const current = docs[key(activeSlug, activeLang)];
  const englishCurrent = docs[key(activeSlug, "en")];
  const translationMissing =
    activeLang !== "en" && (current === null || current === undefined);
  const showEditor = mode === "edit" || mode === "split";
  const showPreview = mode === "preview" || mode === "split";

  return (
    <div
      data-testid="legal-panel"
      className="bg-white border border-[#C9DDD6] rounded-3xl overflow-hidden"
    >
      <div className="p-5 border-b border-[#C9DDD6] flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-lg font-bold text-[#0E2A36]">
              Terms &amp; Policies
            </h2>
            <p className="text-xs text-[#6A8290]">
              Edit the public legal documents shown on /terms-and-policies.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="legal-history-toggle"
              onClick={toggleHistory}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                showHistory
                  ? "bg-[#1F4E5F] text-[#F4FAF8] border-[#1F4E5F]"
                  : "bg-[#F4FAF8] text-[#3D5560] border-[#C9DDD6] hover:border-[#1F4E5F]"
              }`}
            >
              <History className="w-3.5 h-3.5" /> History
            </button>
            <div className="inline-flex items-center gap-1 bg-[#F4FAF8] border border-[#C9DDD6] rounded-full p-1">
              <button
                data-testid="legal-mode-edit"
                onClick={() => setMode("edit")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  mode === "edit"
                    ? "bg-[#1F4E5F] text-[#F4FAF8]"
                    : "text-[#3D5560] hover:bg-[#E1EEEA]"
                }`}
              >
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
              <button
                data-testid="legal-mode-split"
                onClick={() => setMode("split")}
                className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  mode === "split"
                    ? "bg-[#1F4E5F] text-[#F4FAF8]"
                    : "text-[#3D5560] hover:bg-[#E1EEEA]"
                }`}
              >
                <FileText className="w-3.5 h-3.5" /> Split
              </button>
              <button
                data-testid="legal-mode-preview"
                onClick={() => setMode("preview")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  mode === "preview"
                    ? "bg-[#1F4E5F] text-[#F4FAF8]"
                    : "text-[#3D5560] hover:bg-[#E1EEEA]"
                }`}
              >
                <Eye className="w-3.5 h-3.5" /> Preview
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {LEGAL_SECTIONS.map((s) => {
            const active = s.slug === activeSlug;
            return (
              <button
                key={s.slug}
                data-testid={`legal-section-${s.slug}`}
                onClick={() => switchSection(s.slug)}
                className={`inline-flex flex-col items-start text-left px-4 py-2.5 rounded-2xl border transition-all ${
                  active
                    ? "bg-[#1F4E5F] text-[#F4FAF8] border-[#1F4E5F]"
                    : "bg-[#F4FAF8] text-[#0E2A36] border-[#C9DDD6] hover:border-[#1F4E5F]"
                }`}
              >
                <span className="text-sm font-semibold">{s.label}</span>
                <span
                  className={`text-[10px] mt-0.5 ${
                    active ? "text-[#F4FAF8]/80" : "text-[#6A8290]"
                  }`}
                >
                  {s.sub}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-3 flex-wrap pt-1">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#6A8290]">
            <Languages className="w-3 h-3" /> Language
          </span>
          <div
            data-testid="legal-lang-switcher"
            className="flex flex-wrap gap-1.5"
          >
            {languages.map((l) => {
              const isActive = l.code === activeLang;
              const hasTranslation =
                l.code === "en" || (l.available && l.available[activeSlug]);
              return (
                <button
                  key={l.code}
                  data-testid={`legal-lang-${l.code}`}
                  onClick={() => switchLang(l.code)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${
                    isActive
                      ? "bg-[#1F4E5F] text-[#F4FAF8] border-[#1F4E5F]"
                      : hasTranslation
                      ? "bg-[#F4FAF8] text-[#0E2A36] border-[#C9DDD6] hover:border-[#1F4E5F]"
                      : "bg-white text-[#6A8290] border-[#E1EEEA] hover:border-[#1F4E5F]/40"
                  }`}
                  title={hasTranslation ? l.label : `${l.label} — translation missing`}
                >
                  <span>{l.native}</span>
                  {l.code !== "en" && (
                    <span
                      className={`inline-block w-1.5 h-1.5 rounded-full ${
                        hasTranslation
                          ? isActive
                            ? "bg-[#5BAE71]"
                            : "bg-[#5BAE71]"
                          : "bg-[#E8A33D]/60"
                      }`}
                      aria-hidden
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="p-5 sm:p-6 space-y-4">
        {error && (
          <div
            data-testid="legal-error"
            className="rounded-2xl border border-[#F0C4C4] bg-[#FBEDED] text-[#A63333] text-sm px-4 py-3"
          >
            {error}
          </div>
        )}
        {success && (
          <div
            data-testid="legal-success"
            className="rounded-2xl border border-[#BFE2C8] bg-[#EDF8EF] text-[#2E6A3E] text-sm px-4 py-3"
          >
            {success}
          </div>
        )}

        {translationMissing && (
          <div
            data-testid="legal-translation-missing"
            className="rounded-2xl border border-[#E8A33D]/40 bg-[#FBF1DC] text-[#7A571A] text-sm px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
          >
            <span className="leading-relaxed">
              No <strong>{languages.find((l) => l.code === activeLang)?.native || activeLang}</strong> translation yet for this document. Start blank, or copy the English version as a starting point.
            </span>
            <button
              data-testid="legal-copy-english"
              onClick={copyFromEnglish}
              disabled={!englishCurrent}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#1F4E5F] text-[#F4FAF8] hover:bg-[#173E50] disabled:opacity-50"
            >
              <Copy className="w-3.5 h-3.5" /> Copy from English
            </button>
          </div>
        )}

        <div>
          <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A8290] mb-1.5">
            Title
          </label>
          <input
            data-testid="legal-title-input"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            className="w-full px-4 py-2.5 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl text-[#0E2A36] focus:outline-none focus:border-[#1F4E5F]"
            placeholder="Document title"
          />
        </div>

        <div className={`grid gap-4 ${mode === "split" ? "lg:grid-cols-2" : "grid-cols-1"}`}>
          {showEditor && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A8290]">
                  Content (Markdown)
                </label>
              </div>
              <MarkdownToolbar onAction={onToolbarAction} />
              <textarea
                ref={textareaRef}
                data-testid="legal-content-textarea"
                value={draftContent}
                onChange={(e) => setDraftContent(e.target.value)}
                rows={mode === "split" ? 26 : 22}
                spellCheck={false}
                className="mt-2 w-full px-4 py-3 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl text-[#0E2A36] font-mono text-[13px] leading-relaxed focus:outline-none focus:border-[#1F4E5F]"
                placeholder="Write the document content in Markdown…"
              />
              <p className="mt-1.5 text-[11px] text-[#6A8290]">
                Tip: select text first, then click a toolbar button to wrap it.
              </p>
            </div>
          )}
          {showPreview && (
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A8290] mb-1.5">
                Live Preview
              </label>
              <div
                data-testid="legal-preview"
                className="rounded-xl border border-[#C9DDD6] bg-[#F4FAF8] p-5 sm:p-6 max-h-[640px] overflow-y-auto"
              >
                <h3 className="font-display text-xl font-bold text-[#0E2A36]">
                  {draftTitle || "Untitled"}
                </h3>
                <div className="prose prose-careable mt-3 max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {draftContent || "_Nothing to preview yet._"}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-[#E1EEEA]">
          <p className="text-xs text-[#6A8290]">
            {current?.updated_at
              ? `Last updated: ${fmtDate(current.updated_at)}`
              : activeLang === "en"
              ? "Not saved yet"
              : "No translation saved yet"}
          </p>
          <div className="flex items-center gap-2">
            {activeLang !== "en" && current && (
              <button
                data-testid="legal-delete-translation"
                onClick={onDeleteTranslation}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold bg-white border border-[#F0C4C4] text-[#A63333] hover:bg-[#FBEDED] disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" /> Delete translation
              </button>
            )}
            <button
              data-testid="legal-save-button"
              onClick={onSave}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>

        {showHistory && (
          <div
            data-testid="legal-history-panel"
            className="rounded-2xl border border-[#C9DDD6] bg-[#F4FAF8] p-4 sm:p-5 mt-2"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm text-[#0E2A36] inline-flex items-center gap-2">
                <History className="w-4 h-4 text-[#1F4E5F]" /> Version history
                <span className="ml-1 inline-flex items-center text-[10px] font-bold uppercase tracking-[0.16em] text-[#1F4E5F] bg-[#E1EEEA] px-1.5 py-0.5 rounded-full">
                  {languages.find((l) => l.code === activeLang)?.native || activeLang}
                </span>
              </h3>
              <p className="text-[11px] text-[#6A8290]">
                Per-language. Every save & restore is archived for compliance.
              </p>
            </div>
            {historyLoading ? (
              <div className="py-8 flex items-center justify-center text-[#6A8290] text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading versions…
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-[#6A8290] py-2">
                No prior versions yet — the next save will create the first
                archive entry.
              </p>
            ) : (
              <ul className="space-y-2">
                {history.map((v) => {
                  const isViewing = viewingVersion?.version_id === v.version_id;
                  return (
                    <li
                      key={v.version_id}
                      data-testid={`legal-history-item-${v.version_id}`}
                      className="rounded-xl bg-white border border-[#C9DDD6]"
                    >
                      <div className="flex items-center justify-between gap-3 p-3.5 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-[#0E2A36] truncate">
                            {v.title || "Untitled"}
                          </p>
                          <p className="text-[11px] text-[#6A8290] mt-0.5">
                            {fmtDate(v.created_at)} · by {v.created_by}
                            {v.change_note ? ` · ${v.change_note}` : ""}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            data-testid={`legal-history-view-${v.version_id}`}
                            onClick={() =>
                              setViewingVersion(isViewing ? null : v)
                            }
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#F4FAF8] border border-[#C9DDD6] text-[#3D5560] hover:border-[#1F4E5F]"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            {isViewing ? "Hide" : "View"}
                          </button>
                          <button
                            data-testid={`legal-history-restore-${v.version_id}`}
                            onClick={() => onRestore(v.version_id)}
                            disabled={saving}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#1F4E5F] text-[#F4FAF8] hover:bg-[#173E50] disabled:opacity-50"
                          >
                            <RotateCcw className="w-3.5 h-3.5" /> Restore
                          </button>
                        </div>
                      </div>
                      {isViewing && (
                        <div
                          data-testid={`legal-history-preview-${v.version_id}`}
                          className="border-t border-[#E1EEEA] bg-[#F4FAF8] p-4 sm:p-5"
                        >
                          <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6A8290] mb-1">
                            Snapshot preview
                          </p>
                          <div className="prose prose-careable max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {v.content}
                            </ReactMarkdown>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


/* ============ TESTIMONIALS PANEL ============ */
const TESTIMONIAL_STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const TESTIMONIAL_RELATION_LABEL = {
  adult_child: "Adult child",
  senior: "Senior / Parent",
  caregiver: "Caregiver / Family",
  other: "Other",
};

function TestimonialsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [acting, setActing] = useState(null);
  const [counts, setCounts] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });

  const fetchCounts = async () => {
    try {
      const { data } = await api.get("/admin/stats", { headers: authHeaders() });
      setCounts({
        all: data.total_testimonials ?? 0,
        pending: data.pending_testimonials ?? 0,
        approved: data.approved_testimonials ?? 0,
        rejected:
          (data.total_testimonials ?? 0) -
          (data.pending_testimonials ?? 0) -
          (data.approved_testimonials ?? 0),
      });
    } catch {
      /* silent */
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    setError("");
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/admin/testimonials", {
        params,
        headers: authHeaders(),
      });
      setRows(Array.isArray(data) ? data : []);
      fetchCounts();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = filter === "all" ? {} : { status: filter };
        const [{ data: list }, { data: stats }] = await Promise.all([
          api.get("/admin/testimonials", { params, headers: authHeaders() }),
          api.get("/admin/stats", { headers: authHeaders() }),
        ]);
        if (!cancelled) {
          setRows(Array.isArray(list) ? list : []);
          setCounts({
            all: stats.total_testimonials ?? 0,
            pending: stats.pending_testimonials ?? 0,
            approved: stats.approved_testimonials ?? 0,
            rejected:
              (stats.total_testimonials ?? 0) -
              (stats.pending_testimonials ?? 0) -
              (stats.approved_testimonials ?? 0),
          });
          setError("");
        }
      } catch (err) {
        if (!cancelled)
          setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filter]);

  const setStatus = async (id, status) => {
    setActing(id);
    setError("");
    try {
      await api.patch(
        `/admin/testimonials/${id}`,
        { status },
        { headers: authHeaders() }
      );
      await fetchRows();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setActing(null);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this testimonial permanently?")) return;
    setActing(id);
    setError("");
    try {
      await api.delete(`/admin/testimonials/${id}`, { headers: authHeaders() });
      await fetchRows();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setActing(null);
    }
  };

  return (
    <div
      data-testid="testimonials-panel"
      className="bg-white border border-[#C9DDD6] rounded-3xl overflow-hidden"
    >
      <div className="p-5 border-b border-[#C9DDD6] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-[#0E2A36]">
            Testimonials
          </h2>
          <p className="text-xs text-[#6A8290]">
            Approve, reject, or delete reviews. Only approved entries appear on the public site.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1 bg-[#F4FAF8] border border-[#C9DDD6] rounded-full p-1">
            {TESTIMONIAL_STATUS_FILTERS.map((f) => {
              const active = filter === f.value;
              return (
                <button
                  key={f.value}
                  data-testid={`testimonials-filter-${f.value}`}
                  onClick={() => setFilter(f.value)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    active
                      ? "bg-[#1F4E5F] text-[#F4FAF8]"
                      : "text-[#3D5560] hover:bg-[#E1EEEA]"
                  }`}
                >
                  {f.label}
                  <span
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                      active ? "bg-white/20" : "bg-white border border-[#C9DDD6]"
                    }`}
                  >
                    {counts[f.value] ?? 0}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            data-testid="testimonials-refresh"
            onClick={fetchRows}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold bg-[#F4FAF8] border border-[#C9DDD6] text-[#3D5560] hover:border-[#1F4E5F]"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      <div className="p-5">
        {error && (
          <div
            data-testid="testimonials-error"
            className="mb-4 rounded-2xl border border-[#F0C4C4] bg-[#FBEDED] text-[#A63333] text-sm px-4 py-3"
          >
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-16 flex items-center justify-center text-[#6A8290] text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-[#0E2A36] font-semibold">No testimonials in this view</p>
            <p className="text-sm text-[#6A8290] mt-1">
              {filter === "all"
                ? "Once a visitor submits one, it'll show up here."
                : "Try switching to another status filter."}
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="testimonials-list">
            {rows.map((t) => (
              <TestimonialRow
                key={t.id}
                t={t}
                acting={acting === t.id}
                onApprove={() => setStatus(t.id, "approved")}
                onReject={() => setStatus(t.id, "rejected")}
                onMarkPending={() => setStatus(t.id, "pending")}
                onDelete={() => onDelete(t.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TestimonialRow({ t, acting, onApprove, onReject, onMarkPending, onDelete }) {
  const statusStyle = {
    pending: "bg-[#FBF1DC] text-[#7A571A] border-[#E8A33D]/40",
    approved: "bg-[#5BAE71]/15 text-[#2D7A4A] border-[#5BAE71]/40",
    rejected: "bg-[#FBEDED] text-[#A63333] border-[#F0C4C4]",
  }[t.status] || "bg-[#F4FAF8] text-[#3D5560] border-[#C9DDD6]";

  const StatusIcon = {
    pending: Clock,
    approved: CheckCircle2,
    rejected: ThumbsDown,
  }[t.status] || Clock;

  return (
    <li
      data-testid={`testimonial-row-${t.id}`}
      className="rounded-2xl border border-[#C9DDD6] bg-[#F4FAF8] p-4 sm:p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-[#0E2A36] text-sm">{t.name}</p>
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-[0.12em] ${statusStyle}`}
              data-testid={`testimonial-status-${t.id}`}
            >
              <StatusIcon className="w-3 h-3" /> {t.status}
            </span>
          </div>
          <p className="text-xs text-[#6A8290] mt-0.5">
            {TESTIMONIAL_RELATION_LABEL[t.relation] || t.relation}
            {t.city ? ` · ${t.city}` : ""} · {fmtDate(t.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-0.5 text-[#E8A33D]" aria-label={`${t.rating} of 5`}>
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`w-4 h-4 ${i < (t.rating || 0) ? "fill-[#E8A33D]" : "fill-transparent text-[#E8A33D]/40"}`}
            />
          ))}
        </div>
      </div>

      <blockquote className="text-[#0E2A36] text-sm leading-relaxed whitespace-pre-wrap border-l-2 border-[#C9DDD6] pl-3">
        &ldquo;{t.quote}&rdquo;
      </blockquote>

      {t.reviewed_at && (
        <p className="mt-2 text-[11px] text-[#6A8290]">
          Last reviewed {fmtDate(t.reviewed_at)}
          {t.reviewed_by ? ` by ${t.reviewed_by}` : ""}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2 pt-3 border-t border-[#E1EEEA]">
        {t.status !== "approved" && (
          <button
            data-testid={`testimonial-approve-${t.id}`}
            onClick={onApprove}
            disabled={acting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#5BAE71] hover:bg-[#4F9B62] text-white disabled:opacity-50"
          >
            {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsUp className="w-3.5 h-3.5" />}
            Approve
          </button>
        )}
        {t.status !== "rejected" && (
          <button
            data-testid={`testimonial-reject-${t.id}`}
            onClick={onReject}
            disabled={acting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-[#F0C4C4] text-[#A63333] hover:bg-[#FBEDED] disabled:opacity-50"
          >
            <ThumbsDown className="w-3.5 h-3.5" /> Reject
          </button>
        )}
        {t.status !== "pending" && (
          <button
            data-testid={`testimonial-pending-${t.id}`}
            onClick={onMarkPending}
            disabled={acting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-[#C9DDD6] text-[#3D5560] hover:border-[#1F4E5F] disabled:opacity-50"
          >
            <Clock className="w-3.5 h-3.5" /> Mark pending
          </button>
        )}
        <button
          data-testid={`testimonial-delete-${t.id}`}
          onClick={onDelete}
          disabled={acting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-[#F0C4C4] text-[#A63333] hover:bg-[#FBEDED] disabled:opacity-50"
          title="Delete permanently"
        >
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </div>
    </li>
  );
}

/* ============ BLOG PANEL ============ */
function emptyBlogForm() {
  return {
    id: null,
    title: "",
    slug: "",
    excerpt: "",
    cover_image_url: "",
    author: "",
    content: "",
    status: "draft",
  };
}

function BlogPanel() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyBlogForm());
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("split");
  const textareaRef = useRef(null);

  const fetchPosts = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/blog", { headers: authHeaders() });
      setPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startNew = () => {
    setForm(emptyBlogForm());
    setEditing(true);
    setError("");
    setSuccess("");
    setMode("split");
  };

  const startEdit = (p) => {
    setForm({
      id: p.id,
      title: p.title || "",
      slug: p.slug || "",
      excerpt: p.excerpt || "",
      cover_image_url: p.cover_image_url || "",
      author: p.author || "",
      content: p.content || "",
      status: p.status || "draft",
    });
    setEditing(true);
    setError("");
    setSuccess("");
    setMode("split");
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm(emptyBlogForm());
    setError("");
  };

  const onToolbarAction = (action) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const value = ta.value;
    const before = value.slice(0, start);
    const selected = value.slice(start, end);
    const after = value.slice(end);
    const result = runMarkdownAction(action, selected);
    const next = before + result.text + after;
    setField("content", next);
    requestAnimationFrame(() => {
      const node = textareaRef.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(before.length + result.selStart, before.length + result.selEnd);
    });
  };

  const onSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      setError("Title and content are both required.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim() || undefined,
      excerpt: form.excerpt.trim() || null,
      cover_image_url: form.cover_image_url.trim() || null,
      author: form.author.trim() || null,
      content: form.content,
      status: form.status,
    };
    try {
      if (form.id) {
        await api.put(`/admin/blog/${form.id}`, payload, { headers: authHeaders() });
      } else {
        await api.post(`/admin/blog`, payload, { headers: authHeaders() });
      }
      setSuccess("Post saved.");
      setEditing(false);
      setForm(emptyBlogForm());
      fetchPosts();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (p) => {
    const status = p.status === "published" ? "draft" : "published";
    setError("");
    try {
      await api.put(
        `/admin/blog/${p.id}`,
        {
          title: p.title,
          slug: p.slug,
          excerpt: p.excerpt,
          cover_image_url: p.cover_image_url,
          author: p.author,
          content: p.content,
          status,
        },
        { headers: authHeaders() }
      );
      fetchPosts();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    setError("");
    try {
      await api.delete(`/admin/blog/${id}`, { headers: authHeaders() });
      fetchPosts();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    }
  };

  const showEditor = mode === "edit" || mode === "split";
  const showPreview = mode === "preview" || mode === "split";

  return (
    <div data-testid="blog-panel" className="bg-white border border-[#C9DDD6] rounded-3xl overflow-hidden">
      <div className="p-5 border-b border-[#C9DDD6] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-[#0E2A36]">Blog</h2>
          <p className="text-xs text-[#6A8290]">
            Write, publish, and manage the blog posts shown on /blog.
          </p>
        </div>
        {!editing && (
          <button
            data-testid="blog-new-button"
            onClick={startNew}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8]"
          >
            <Plus className="w-4 h-4" /> New post
          </button>
        )}
      </div>

      <div className="p-5">
        {error && (
          <div data-testid="blog-error" className="mb-4 rounded-2xl border border-[#F0C4C4] bg-[#FBEDED] text-[#A63333] text-sm px-4 py-3">
            {error}
          </div>
        )}
        {success && !editing && (
          <div data-testid="blog-success" className="mb-4 rounded-2xl border border-[#BFE2C8] bg-[#EDF8EF] text-[#2E6A3E] text-sm px-4 py-3">
            {success}
          </div>
        )}

        {editing ? (
          <div data-testid="blog-editor" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <button
                data-testid="blog-back-to-list"
                onClick={cancelEdit}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#3D5560] hover:text-[#1F4E5F]"
              >
                <ArrowLeft className="w-4 h-4" /> Back to posts
              </button>
              <div className="inline-flex items-center gap-1 bg-[#F4FAF8] border border-[#C9DDD6] rounded-full p-1">
                <button onClick={() => setMode("edit")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${mode === "edit" ? "bg-[#1F4E5F] text-[#F4FAF8]" : "text-[#3D5560] hover:bg-[#E1EEEA]"}`}>Edit</button>
                <button onClick={() => setMode("split")} className={`hidden sm:inline-flex px-3 py-1.5 rounded-full text-xs font-semibold ${mode === "split" ? "bg-[#1F4E5F] text-[#F4FAF8]" : "text-[#3D5560] hover:bg-[#E1EEEA]"}`}>Split</button>
                <button onClick={() => setMode("preview")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${mode === "preview" ? "bg-[#1F4E5F] text-[#F4FAF8]" : "text-[#3D5560] hover:bg-[#E1EEEA]"}`}>Preview</button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A8290] mb-1.5">Title *</label>
                <input data-testid="blog-title-input" value={form.title} onChange={(e) => setField("title", e.target.value)} className="w-full px-4 py-2.5 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl focus:outline-none focus:border-[#1F4E5F]" placeholder="Post title" />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A8290] mb-1.5">Slug (optional)</label>
                <input data-testid="blog-slug-input" value={form.slug} onChange={(e) => setField("slug", e.target.value)} className="w-full px-4 py-2.5 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl focus:outline-none focus:border-[#1F4E5F]" placeholder="auto-generated-from-title" />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A8290] mb-1.5">Author (optional)</label>
                <input data-testid="blog-author-input" value={form.author} onChange={(e) => setField("author", e.target.value)} className="w-full px-4 py-2.5 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl focus:outline-none focus:border-[#1F4E5F]" placeholder="e.g. Careable Team" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A8290] mb-1.5">Cover image URL (optional)</label>
                <input data-testid="blog-cover-input" value={form.cover_image_url} onChange={(e) => setField("cover_image_url", e.target.value)} className="w-full px-4 py-2.5 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl focus:outline-none focus:border-[#1F4E5F]" placeholder="https://…" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A8290] mb-1.5">Excerpt (optional)</label>
                <textarea data-testid="blog-excerpt-input" value={form.excerpt} onChange={(e) => setField("excerpt", e.target.value)} rows={2} className="w-full px-4 py-2.5 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl focus:outline-none focus:border-[#1F4E5F]" placeholder="Short summary shown on the blog list card." />
              </div>
            </div>

            <div className={`grid gap-4 ${mode === "split" ? "lg:grid-cols-2" : "grid-cols-1"}`}>
              {showEditor && (
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A8290] mb-1.5">Content (Markdown) *</label>
                  <MarkdownToolbar onAction={onToolbarAction} />
                  <textarea ref={textareaRef} data-testid="blog-content-textarea" value={form.content} onChange={(e) => setField("content", e.target.value)} rows={mode === "split" ? 20 : 18} spellCheck={false} className="mt-2 w-full px-4 py-3 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl font-mono text-[13px] leading-relaxed focus:outline-none focus:border-[#1F4E5F]" placeholder="Write your post in Markdown…" />
                </div>
              )}
              {showPreview && (
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A8290] mb-1.5">Live Preview</label>
                  <div data-testid="blog-preview" className="rounded-xl border border-[#C9DDD6] bg-[#F4FAF8] p-5 max-h-[560px] overflow-y-auto">
                    <h3 className="font-display text-xl font-bold text-[#0E2A36]">{form.title || "Untitled"}</h3>
                    <div className="prose prose-careable mt-3 max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content || "_Nothing to preview yet._"}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3 pt-3 border-t border-[#E1EEEA]">
              <div className="inline-flex items-center gap-1 bg-[#F4FAF8] border border-[#C9DDD6] rounded-full p-1" data-testid="blog-status-toggle">
                <button data-testid="blog-status-draft" onClick={() => setField("status", "draft")} className={`px-3.5 py-1.5 rounded-full text-xs font-semibold ${form.status === "draft" ? "bg-[#E8A33D] text-white" : "text-[#3D5560] hover:bg-[#E1EEEA]"}`}>Draft</button>
                <button data-testid="blog-status-published" onClick={() => setField("status", "published")} className={`px-3.5 py-1.5 rounded-full text-xs font-semibold ${form.status === "published" ? "bg-[#5BAE71] text-white" : "text-[#3D5560] hover:bg-[#E1EEEA]"}`}>Published</button>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="blog-cancel-button" onClick={cancelEdit} className="px-4 py-2 rounded-full text-sm font-semibold bg-white border border-[#C9DDD6] text-[#3D5560] hover:border-[#1F4E5F]">Cancel</button>
                <button data-testid="blog-save-button" onClick={onSave} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] disabled:opacity-50">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? "Saving…" : form.id ? "Save changes" : "Create post"}
                </button>
              </div>
            </div>
          </div>
        ) : loading ? (
          <div className="py-16 flex items-center justify-center text-[#6A8290] text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : posts.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-[#0E2A36] font-semibold">No posts yet</p>
            <p className="text-sm text-[#6A8290] mt-1">Click “New post” to write your first blog entry.</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="blog-list">
            {posts.map((p) => (
              <li key={p.id} data-testid={`blog-row-${p.id}`} className="rounded-2xl border border-[#C9DDD6] bg-[#F4FAF8] p-4 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-[#0E2A36] text-sm">{p.title}</p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-[0.12em] ${p.status === "published" ? "bg-[#5BAE71]/15 text-[#2D7A4A] border-[#5BAE71]/40" : "bg-[#FBF1DC] text-[#7A571A] border-[#E8A33D]/40"}`}>{p.status}</span>
                  </div>
                  <p className="text-xs text-[#6A8290] mt-0.5">/blog/{p.slug} · Updated {fmtDate(p.updated_at)}{p.author ? ` · ${p.author}` : ""}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button data-testid={`blog-toggle-${p.id}`} onClick={() => toggleStatus(p)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-[#C9DDD6] text-[#3D5560] hover:border-[#1F4E5F]">
                    {p.status === "published" ? "Unpublish" : "Publish"}
                  </button>
                  <button data-testid={`blog-edit-${p.id}`} onClick={() => startEdit(p)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#1F4E5F] text-[#F4FAF8] hover:bg-[#173E50]">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button data-testid={`blog-delete-${p.id}`} onClick={() => onDelete(p.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-[#F0C4C4] text-[#A63333] hover:bg-[#FBEDED]" title="Delete permanently">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
